import React, { useState, useEffect } from 'react';
import { FiX, FiStar, FiPlus } from 'react-icons/fi';
import axios from 'axios';
import './PaidFeedbackModal.css';

const PaidFeedbackModal = ({ feedback, onClose, onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    IdClassStatus: 0,
    Projects: [{
      ModuleId: '',
      ProjectId: ''
    }],
    Rating: 0,
    Details: '',
    PTMHappened: (feedback.sessionsremain === 1 && !(Number(feedback.notScheduled) > 0)) ? true : false,    PTMNotes: ''
  });

  const [modules, setModules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [loadingModules, setLoadingModules] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const classStatusOptions = [
    { value: 0, label: 'Class Completed' },
    { value: 1, label: 'Student Absent' },
    { value: 2, label: 'Student Left' },
    { value: 3, label: 'Technical Issues' }
  ];

  // Fetch modules on mount
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teacher/Curriculum/modules/${feedback.IdGradesPlans}`
        );
        setModules(response.data);
      } catch (error) {
        console.error('Error fetching modules:', error);
        setSubmitError('Failed to load modules');
      } finally {
        setLoadingModules(false);
      }
    };

    fetchModules();
  }, [feedback.IdGradesPlans]);

  // Fetch projects when module is selected
  const fetchProjects = async (moduleId, projectIndex) => {
    try {
      setLoadingProjects(true);
      const response = await axios.get(
       `${process.env.REACT_APP_API_BASE_URL}/api/teacher/Curriculum/paid/${moduleId}/${feedback.IdGradesPlans}`
      );
      
      setProjects(prev => {
        const newProjects = [...prev];
        newProjects[projectIndex] = response.data.courses;
        return newProjects;
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
      setSubmitError('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleModuleChange = (e, index) => {
    const moduleId = parseInt(e.target.value);
    const newProjects = [...formData.Projects];
    newProjects[index] = { ModuleId: moduleId, ProjectId: '' };
    
    setFormData({
      ...formData,
      Projects: newProjects
    });

    if (moduleId) {
      fetchProjects(moduleId, index);
    }
  };

  const handleProjectChange = (e, index) => {
    const projectId = parseInt(e.target.value);
    const newProjects = [...formData.Projects];
    newProjects[index].ProjectId = projectId;
    
    setFormData({
      ...formData,
      Projects: newProjects
    });
  };

  const addProjectField = () => {
    if (formData.Projects.length < 2) {
        setFormData({
          ...formData,
          Projects: [...formData.Projects, { ModuleId: '', ProjectId: '' }]
        });
      }
  };

  const removeProjectField = (index) => {
    if (formData.Projects.length <= 1) return;
    
    const newProjects = [...formData.Projects];
    newProjects.splice(index, 1);
    
    setFormData({
      ...formData,
      Projects: newProjects
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate at least one project is selected
      const hasEmptyProjects = formData.Projects.some(
        project => !project.ModuleId || !project.ProjectId
      );
      
      if (formData.IdClassStatus===0 && hasEmptyProjects) {
        throw new Error('Please select a module and project for all entries');
      }
      if (formData.IdClassStatus===0 && formData.Rating === 0) {
        throw new Error('Please provide a class rating');
      }
      if (formData.IdClassStatus===0 && !formData.Details.trim()) {
        throw new Error('Notes are required');
      } 
      if (formData.IdClassStatus===0 && (feedback.sessionsremain === 1 && !(Number(feedback.notScheduled) > 0)) && formData.PTMHappened && !formData.PTMNotes.trim()) {
        throw new Error('PTM Notes are required when PTM happened');
      }
      const submissionData = {
        ClassDateTime: feedback.ClassDateTime,
        IdSchedule: feedback.IdSchedule,
        IdTeacher: feedback.IdTeacher,
        IdStudent: feedback.IdStudent,
        IdClassStatus: formData.IdClassStatus,
        Projects: formData.Projects,
        Rating: formData.Rating,
        Details: formData.Details,
        PTMHappened: (feedback.sessionsremain === 1 && !(Number(feedback.notScheduled) > 0)) ? formData.PTMHappened : null,
        PTMNotes: (feedback.sessionsremain === 1 && !(Number(feedback.notScheduled) > 0)) ? formData.PTMNotes : null
      };

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/teacher/submitpaidform`,
        submissionData
      );

      onSubmitSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting paid feedback:', error);
      setSubmitError(error.response?.data?.error || error.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };
  const isClassCompleted = formData.IdClassStatus === 0;

  return (
    <div className="modal-overlayPaid">
      <div className="modal-content paid-feedback-modal">
        <div className="modal-header">
          <h3>Paid Class Feedback</h3>
          <button 
            onClick={onClose} 
            className="close-btn"
            disabled={isSubmitting}
          >
            <FiX />
          </button>
        </div>

        <div className="student-info">
          <p><strong>Student:</strong> {feedback.FirstName} {feedback.LastName}</p>
          <p><strong>Date:</strong> {new Date(feedback.ClassDateTime).toLocaleString()}</p>
          {(feedback.sessionsremain === 1 && !(Number(feedback.notScheduled) > 0)) && (
            <p className="ptm-notice">Last session - PTM required</p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Class Status *</label>
            <select
              name="IdClassStatus"
              value={formData.IdClassStatus}
              onChange={(e) => setFormData({
                ...formData, 
                IdClassStatus: parseInt(e.target.value)
              })}
              className="form-select"
              required
              disabled={isSubmitting}
            >
              {classStatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {isClassCompleted ? (
            <>
              {/* Projects Section - Only shown when class is completed */}
              <div className="projects-section">
                <label>Projects *</label>
                {formData.Projects.map((project, index) => (
                  <>
                    <div key={index} className="project-row">
                      <div className="form-group">
                        <select
                          value={project.ModuleId}
                          onChange={(e) => handleModuleChange(e, index)}
                          className="form-select"
                          required
                          disabled={isSubmitting || loadingModules}
                        >
                          <option value="">Select Module</option>
                          {modules.map(module => (
                            <option key={module.Id} value={module.Id}>
                              {module.ModuleName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <select
                          value={project.ProjectId}
                          onChange={(e) => handleProjectChange(e, index)}
                          className="form-select"
                          required
                          disabled={isSubmitting || loadingProjects || !project.ModuleId}
                        >
                          <option value="">Select Project</option>
                          {projects[index]?.map(project => (
                            <option key={project.Id} value={project.Id}>
                              {project.CourseName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      {index > 0 && (
                        <button
                          type="button"
                          className="add-project-btn"
                          onClick={() => removeProjectField(index)}
                          disabled={isSubmitting}
                        >
                          - Remove Project
                        </button>
                      )}
                    </div>
                  </>
                ))}
                
                {formData.Projects.length < 2 && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="add-project-btn"
                      onClick={addProjectField}
                      disabled={isSubmitting}
                    >
                      <FiPlus /> Add Another Project
                    </button>
                  </div>
                )}
              </div>

              {/* Rating - Only shown when class is completed */}
              {isClassCompleted && (
                <div className="form-group">
                <label>Class Rating (1-5) *</label>
                <div className="rating-stars">
                    {[1, 2, 3, 4, 5].map(star => (
                    <FiStar
                        key={star}
                        className={`star ${star <= formData.Rating ? 'filled' : ''}`}
                        onClick={() => setFormData({...formData, Rating: star})}
                    />
                    ))}
                </div>
                {submitError?.includes('rating') && (
                    <div className="error-text">Please select a rating</div>
                )}
                </div>
            )}

              {/* PTM Section - Only shown when class is completed and last session */}
              {(feedback.sessionsremain === 1 && !(Number(feedback.notScheduled) > 0)) && (
                <>
                  <div className="form-group">
                    <label>Did PTM happen? *</label>
                    <div className="radio-group">
                      <label>
                        <input
                          type="radio"
                          name="PTMHappened"
                          checked={formData.PTMHappened === true}
                          onChange={() => setFormData({...formData, PTMHappened: true})}
                          disabled={isSubmitting}
                        />
                        Yes
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="PTMHappened"
                          checked={formData.PTMHappened === false}
                          onChange={() => setFormData({...formData, PTMHappened: false})}
                          disabled={isSubmitting}
                        />
                        No
                      </label>
                    </div>
                  </div>

                  {formData.PTMHappened && (
                <div className="form-group">
                <label>PTM Notes *</label>
                <textarea
                    name="PTMNotes"
                    value={formData.PTMNotes}
                    onChange={(e) => setFormData({
                    ...formData, 
                    PTMNotes: e.target.value
                    })}
                    className="form-textarea"
                    placeholder="Enter PTM discussion points..."
                    required={formData.PTMHappened}
                    disabled={isSubmitting}
                />
                </div>
                )}
                </>
              )}
            </>
          ) : null}

          {/* Notes - Always shown */}
          <div className="form-group">
            <label>Notes *</label>
            <textarea
              name="Details"
              value={formData.Details}
              onChange={(e) => setFormData({
                ...formData, 
                Details: e.target.value
              })}
              className="form-textarea"
              placeholder={
                isClassCompleted 
                  ? "Enter class notes, progress, and next steps..."
                  : "Enter reason for not completing the class..."
              }
              required
              disabled={isSubmitting}
            />
          </div>

       

          {submitError && (
            <div className="error-message">
              {submitError}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaidFeedbackModal;